/**
 * Gemini Live setup tunables — the vocabulary the per-model capability table
 * in `GeminiLiveModels.schema.ts` is written in.
 *
 * A tunable is one field the Live API lets a session configure at setup (or on
 * a tool declaration / tool response). Each model states, per tunable, one of
 * three things, and the setup builder and the settings UI both read that one
 * statement rather than branching on a model id:
 *
 *   - `configurable` — the model accepts a choice among `values`. The UI may
 *     show it; the builder sends the deployment's choice. `providerDefault` is
 *     what the model does when the field is omitted, per the cited page.
 *   - `fixed`        — the model has exactly one behaviour and documents that
 *     asking for another one is an error. Nothing to show; the builder must
 *     never send anything but `value`, and omitting the field is safe.
 *   - `unsupported`  — the model's documentation says to omit the field. The
 *     UI must not show it and the builder must not send it.
 *
 * Every entry carries `doc` — the page that states it — and `wire`: whether a
 * RECORDED session has confirmed what the model does when the rule is broken.
 * `provisional` means only the documentation has been read. A provider's prose
 * pages contradict each other and lag its API reference, so a `provisional`
 * row is a claim about Google's docs, never about Google's socket.
 */

// ============================================================
// Value vocabularies
// ============================================================

/**
 * Gemini Live thinking levels — subset of GEMINI_EFFORT_LEVELS without "dynamic"
 * which is not supported by the Live API.
 * Controls how much reasoning the model does before responding.
 * Higher = better quality but higher latency.
 * @see Big Bench Audio: 3.1 Flash Live (Thinking High) = 95.9% vs (Thinking Minimal) = 70.5%
 */
export const GEMINI_LIVE_THINKING_LEVELS = ["minimal", "low", "medium", "high"] as const;
export type GeminiLiveThinkingLevel = (typeof GEMINI_LIVE_THINKING_LEVELS)[number];

/**
 * `FunctionDeclaration.behavior` — whether the model waits for a tool's
 * response before it continues the conversation. Spelled as the API
 * reference's enum (`UNSPECIFIED` is documented as unused and is omitted).
 *
 * @see https://ai.google.dev/api/generate-content — `Behavior`
 */
export const GEMINI_LIVE_FUNCTION_BEHAVIORS = ["BLOCKING", "NON_BLOCKING"] as const;
export type GeminiLiveFunctionBehavior = (typeof GEMINI_LIVE_FUNCTION_BEHAVIORS)[number];

/**
 * `FunctionResponse.scheduling` — how a NON_BLOCKING tool's response enters
 * the conversation. Spelled as the API reference's enum: `INTERRUPT`. The
 * gemini-3.8-live model page spells the same value `INTERRUPTED`; the reference
 * outranks a model page, and this is the spelling that goes on the wire.
 *
 * @see https://ai.google.dev/api/generate-content — `Scheduling`
 * @see https://ai.google.dev/gemini-api/docs/models/gemini-3.8-live — the other spelling
 */
export const GEMINI_LIVE_FUNCTION_RESPONSE_SCHEDULINGS = [
  "SILENT",
  "WHEN_IDLE",
  "INTERRUPT"
] as const;
export type GeminiLiveFunctionResponseScheduling =
  (typeof GEMINI_LIVE_FUNCTION_RESPONSE_SCHEDULINGS)[number];

/**
 * `realtimeInputConfig.activityHandling` — whether the caller's speech cuts the
 * model off. Spelled as the API reference's enum; `ACTIVITY_HANDLING_UNSPECIFIED`
 * is documented as meaning `START_OF_ACTIVITY_INTERRUPTS` and is never sent.
 *
 * @see https://ai.google.dev/api/live — `ActivityHandling`
 */
export const GEMINI_LIVE_ACTIVITY_HANDLINGS = [
  "START_OF_ACTIVITY_INTERRUPTS",
  "NO_INTERRUPTION"
] as const;
export type GeminiLiveActivityHandling = (typeof GEMINI_LIVE_ACTIVITY_HANDLINGS)[number];

// ============================================================
// Support states
// ============================================================

/**
 * Whether a recorded session has confirmed the rule on the socket.
 * `provisional` — documentation only; `confirmed` — a recorded fixture shows it.
 */
export const GEMINI_LIVE_WIRE_EVIDENCE = ["provisional", "confirmed"] as const;
export type GeminiLiveWireEvidence = (typeof GEMINI_LIVE_WIRE_EVIDENCE)[number];

interface GeminiLiveTunableProvenance {
  /** The documentation page that states this model's rule for this tunable. */
  doc: string;
  wire: GeminiLiveWireEvidence;
}

export interface GeminiLiveConfigurableTunable<V extends string | boolean>
  extends GeminiLiveTunableProvenance {
  support: "configurable";
  values: readonly V[];
  /** What the model does when the field is omitted from setup. */
  providerDefault: V;
}

export interface GeminiLiveFixedTunable<V extends string | boolean>
  extends GeminiLiveTunableProvenance {
  support: "fixed";
  value: V;
}

export interface GeminiLiveUnsupportedTunable extends GeminiLiveTunableProvenance {
  support: "unsupported";
}

export type GeminiLiveTunable<V extends string | boolean> =
  | GeminiLiveConfigurableTunable<V>
  | GeminiLiveFixedTunable<V>
  | GeminiLiveUnsupportedTunable;

/**
 * A tunable whose value is open rather than one of a list — a duration, a list
 * of phrases, a language code. `configurable` obliges the same as above: the
 * UI may show it and the builder sends the deployment's stored value.
 */
export interface GeminiLiveOpenConfigurableTunable extends GeminiLiveTunableProvenance {
  support: "configurable";
}

export type GeminiLiveOpenTunable =
  | GeminiLiveOpenConfigurableTunable
  | GeminiLiveUnsupportedTunable;

/**
 * Every setup tunable a deployment's settings can reach, per model — the rows
 * the settings UI shows or hides and the setup builder sends or omits. A row
 * that is `configurable` on a model is a promise that a stored value reaches
 * that model's setup frame; `gemini-live-settings-reach-the-wire.spec.ts` holds
 * the builder to it for every row.
 *
 * Fields the builder sends identically on every model with no deployment
 * choice behind them (voice, temperature, context compression, session
 * resumption) are not listed.
 */
export interface GeminiLiveTunables {
  /** `generationConfig.thinkingConfig.thinkingLevel`. */
  thinkingLevel: GeminiLiveTunable<GeminiLiveThinkingLevel>;
  /** `setup.proactivity.proactiveAudio`. */
  proactiveAudio: GeminiLiveTunable<boolean>;
  /** `generationConfig.enableAffectiveDialog`. */
  affectiveDialog: GeminiLiveTunable<boolean>;
  /** `tools[].functionDeclarations[].behavior`. */
  functionBehavior: GeminiLiveTunable<GeminiLiveFunctionBehavior>;
  /** `toolResponse.functionResponses[].scheduling` — NON_BLOCKING calls only. */
  functionResponseScheduling: GeminiLiveTunable<GeminiLiveFunctionResponseScheduling>;
  /**
   * `toolResponse.functionResponses[].willContinue` — whether a NON_BLOCKING
   * call can be answered more than once, an interim (`true`) ahead of its final
   * result (`false`). `configurable` is the model honouring it as a generator;
   * `unsupported` is a model that takes the first response as THE result.
   */
  functionResponseWillContinue: GeminiLiveTunable<boolean>;
  /** `realtimeInputConfig.activityHandling`, from `geminiTurnDetection.interruptResponse`. */
  activityHandling: GeminiLiveTunable<GeminiLiveActivityHandling>;
  /** `realtimeInputConfig.automaticActivityDetection.silenceDurationMs`. */
  silenceDurationMs: GeminiLiveOpenTunable;
  /** `realtimeInputConfig.automaticActivityDetection.prefixPaddingMs`. */
  prefixPaddingMs: GeminiLiveOpenTunable;
  /** `inputAudioTranscription.customVocabulary[]`, from `geminiCustomVocabulary`. */
  customVocabulary: GeminiLiveOpenTunable;
  /** `inputAudioTranscription.languageCodes[]`, from the deployment's `language`. */
  inputLanguageCodes: GeminiLiveOpenTunable;
}
